import { FC, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { useBackButtonToPrevRoute } from '@/hooks/useBackButtonToPrevRoute.tsx';
import { routes } from '@/navigation/routes.tsx';
import { Steps } from '@/components/common/Steps/Steps.tsx';
import { UserSelfie } from '@/components/Registration/UserSelfie.tsx';
import { FileData } from '@/shared/interface/userRegistrationData.ts';
import { UserDataFields } from '@/shared/type/userDataFields.ts';
import ProfileController from '@/api/profile/profile.ts';
import { useProfileStore } from '@/store/useProfileStore.ts';
import { catchErrors } from '@/utils/sentry-monitor.tsx';
import { MainButton } from '@/components/common/MainButton/MainButton.tsx';

export type UserSelfieFields = {
  selfie?: FileData;
};

const RegistrationUserSelfie: FC = () => {
  useBackButtonToPrevRoute(routes.registrationUserPhotos, false);
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { updateProfile } = useProfileStore();

  const [data, setData] = useState<UserSelfieFields>({
    selfie: undefined
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const methods = useForm({
    defaultValues: data
  });

  function updateFields(fields: Partial<UserDataFields>) {
    setData((prev) => ({ ...prev, ...fields }));
  }

  const onSubmit = useCallback(async () => {
    if (!data.selfie?.file || isSubmitting) {
      return;
    }

    try {
      setIsSubmitting(true);
      const file = data.selfie.file;
      const mimeType = file.type || 'image/jpeg';
      const { upload_url, photo_hid } = await ProfileController.prepareSelfie(
        mimeType,
        file.size
      );
      await ProfileController.uploadToS3(upload_url, file, mimeType);
      await ProfileController.completePhoto(photo_hid, mimeType, file.size);
      await ProfileController.submitPhotoToReview();
      await updateProfile();

      navigate(routes.search);
    } catch (e) {
      setIsSubmitting(false);
      catchErrors(e, true);
    }
  }, [data.selfie?.file, isSubmitting, updateProfile, navigate]);

  return (
    <FormProvider {...methods}>
      <form style={{ paddingTop: '100px', paddingBottom: '80px' }}>
        <Steps count={3} progress={3} />
        <UserSelfie
          {...data}
          updateFields={updateFields}
          onSubmit={onSubmit}
          isSubmitting={isSubmitting}
        />
      </form>
      {data.selfie?.file && (
        <MainButton
          onClick={onSubmit}
          text={t('button.finish')}
          loading={isSubmitting}
          disabled={isSubmitting}
          fixed
        />
      )}
    </FormProvider>
  );
};

export default RegistrationUserSelfie;
